import { API_URL, getAuthHeaders } from './config';

export const adminService = {
    listUsers: async () => {
        const res = await fetch(`${API_URL}/admin/users`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch users");
        const users = await res.json();

        // Fix relative URLs for all users
        const baseUrl = API_URL.replace(/\/api$/, '');
        return users.map((u: any) => {
            if (u.profile_pic_url && u.profile_pic_url.startsWith('/')) {
                return { ...u, profile_pic_url: `${baseUrl}${u.profile_pic_url}` };
            }
            return u;
        });
    },
    deleteUser: async (id: string) => {
        const res = await fetch(`${API_URL}/admin/users/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to delete user");
        return res.json();
    },
    updateUser: async (id: string, data: any) => {
        const res = await fetch(`${API_URL}/admin/users/${id}`, {
            method: 'PUT',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to update user");
        return res.json();
    },

    getReports: async () => {
        const res = await fetch(`${API_URL}/admin/reports`, { headers: getAuthHeaders() });
        if (!res.ok) throw new Error('Failed to fetch reports');
        return await res.json();
    },

    resolveReport: async (reportId: string, action: 'dismiss' | 'delete') => {
        const res = await fetch(`${API_URL}/admin/reports/${reportId}`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ action })
        });
        if (!res.ok) throw new Error('Failed to resolve report');
        return await res.json();
    },
    listMatches: async (type: 'upcoming' | 'history' = 'upcoming') => {
        const res = await fetch(`${API_URL}/admin/matches?type=${type}`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch matches");
        return res.json();
    },
    getMatch: async (id: number) => {
        const res = await fetch(`${API_URL}/admin/matches/${id}`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch match details");
        const data = await res.json();
        // Fix relative URLs for players
        const baseUrl = API_URL.replace(/\/api$/, '');
        if (data.players) {
            data.players = data.players.map((p: any) => {
                if (p.profile_pic_url && p.profile_pic_url.startsWith('/')) {
                    p.profile_pic_url = `${baseUrl}${p.profile_pic_url}`;
                }
                return p;
            });
        }
        return data;
    },
    listClaims: async (status?: 'pending' | 'approved' | 'rejected') => {
        const query = status ? `?status=${status}` : '';
        const res = await fetch(`${API_URL}/admin/claims${query}`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch claims");
        return res.json();
    },
    processClaim: async (id: number, action: 'approve' | 'reject', notes?: string) => {
        const res = await fetch(`${API_URL}/admin/claims/${id}`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ action, notes })
        });
        if (!res.ok) throw new Error("Failed to process claim");
        return res.json();
    },
    getStats: async () => {
        const res = await fetch(`${API_URL}/admin/stats`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch dashboard stats");
        return res.json();
    },

    // Push Subscriptions
    listPushSubscriptions: async () => {
        const res = await fetch(`${API_URL}/admin/push-subscriptions`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch push subscriptions");
        return res.json();
    },

    // Club Requests
    listClubRequests: async () => {
        const res = await fetch(`${API_URL}/admin/club-requests`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch club requests");
        return res.json();
    },
    processClubRequest: async (id: number, action: 'approve' | 'reject', notes?: string, clubData?: any) => {
        const res = await fetch(`${API_URL}/admin/club-requests/${id}/process`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ action, notes, clubData })
        });
        if (!res.ok) throw new Error("Failed to process request");
        return res.json();
    },
    updateClub: async (id: string, data: any) => {
        const res = await fetch(`${API_URL}/admin/clubs/${id}`, {
            method: 'PUT',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to update club");
        return res.json();
    },

    revokeManager: async (userId: string, clubId: string) => {
        const res = await fetch(`${API_URL}/admin/users/${userId}/revoke`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ clubId })
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to revoke manager");
        }
        return res.json();
    },
    // Referee Validation
    listPendingReferees: async () => {
        const res = await fetch(`${API_URL}/admin/referee/pending`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch pending referees");
        return res.json();
    },
    getRefereeHistory: async () => {
        const res = await fetch(`${API_URL}/admin/referee/history`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch referee history");
        return res.json();
    },
    validateReferee: async (referee_user_id: string, status: 'verified' | 'rejected') => {
        const res = await fetch(`${API_URL}/admin/referee/validate`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ referee_user_id, status })
        });
        if (!res.ok) throw new Error("Failed to validate referee");
        return res.json();
    },

    // Contact Messages
    getMessages: async (status?: string, limit = 50, offset = 0) => {
        let query = `?limit=${limit}&offset=${offset}`;
        if (status) query += `&status=${status}`;

        const res = await fetch(`${API_URL}/admin/messages${query}`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch messages");
        return res.json();
    },
    replyToMessage: async (id: number, replyMessage: string) => {
        const res = await fetch(`${API_URL}/admin/messages/${id}/reply`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ replyMessage })
        });
        if (!res.ok) throw new Error("Failed to send reply");
        return res.json();
    },

    // Shops/Merchants Validation
    listShops: async () => {
        const res = await fetch(`${API_URL}/admin/marketplace/shops`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch shop requests");
        return res.json();
    },
    processShop: async (id: string, action: 'approved' | 'rejected' | 'suspended', notes?: string) => {
        const res = await fetch(`${API_URL}/admin/marketplace/shops/${id}/process`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ action, notes })
        });
        if (!res.ok) throw new Error("Failed to process shop request");
        return res.json();
    },

    getStripeStatus: async () => {
        const res = await fetch(`${API_URL}/admin/stripe/platform-status`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch platform Stripe status");
        return res.json();
    },
    magicImport: async (url: string) => {
        const res = await fetch(`${API_URL}/admin/clubs/magic-import`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ url })
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Magic Import failed");
        }
        return res.json();
    },
    bulkImport: async (items: { url: string; banner_url?: string }[]) => {
        const res = await fetch(`${API_URL}/admin/clubs/bulk-import`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ items })
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Bulk Import failed");
        }
        return res.json();
    },
    listProClaims: async () => {
        const res = await fetch(`${API_URL}/admin/pro-claims`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch pro claims");
        const claims = await res.json();

        // Fix relative URLs for user avatars
        const baseUrl = API_URL.replace(/\/api$/, '');
        return claims.map((c: any) => {
            if (c.user_avatar && c.user_avatar.startsWith('/')) {
                return { ...c, user_avatar: `${baseUrl}${c.user_avatar}` };
            }
            return c;
        });
    },
    processProClaim: async (id: number, action: 'approve' | 'reject', notes?: string) => {
        const res = await fetch(`${API_URL}/admin/pro-claims/${id}/process`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ action, notes })
        });
        if (!res.ok) throw new Error("Failed to process pro claim");
        return res.json();
    },
    listProPlayers: async () => {
        const res = await fetch(`${API_URL}/admin/users/pro`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch pro players");
        const players = await res.json();

        // Fix relative URLs for all players
        const baseUrl = API_URL.replace(/\/api$/, '');
        return players.map((p: any) => {
            if (p.profile_pic_url && p.profile_pic_url.startsWith('/')) {
                return { ...p, profile_pic_url: `${baseUrl}${p.profile_pic_url}` };
            }
            return p;
        });
    },

    triggerProMonthlyReport: async (debug: boolean = false) => {
        const res = await fetch(`${API_URL}/admin/pro/trigger-report`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ debug })
        });
        if (!res.ok) throw new Error("Failed to trigger pro monthly report");
        return res.json();
    },
    triggerProDataSync: async () => {
        const res = await fetch(`${API_URL}/admin/pro/sync-data`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to trigger pro data sync");
        return res.json();
    },
    syncFederationsFromRegistry: async () => {
        const res = await fetch(`${API_URL}/admin/federations/sync-registry`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to synchronize federations");
        return res.json();
    },

    // Social Media Agent
    listSocialProposals: async () => {
        const res = await fetch(`${API_URL}/admin/social/proposals`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch social proposals");
        return res.json();
    },
    getSocialProposal: async (id: string) => {
        const res = await fetch(`${API_URL}/admin/social/proposals/${id}`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch social proposal");
        return res.json();
    },
    approveSocialProposal: async (id: string, caption: string) => {
        const res = await fetch(`${API_URL}/admin/social/proposals/${id}/approve`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ caption })
        });
        if (!res.ok) throw new Error("Failed to approve social proposal");
        return res.json();
    },
    rejectSocialProposal: async (id: string) => {
        const res = await fetch(`${API_URL}/admin/social/proposals/${id}/reject`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to reject social proposal");
        return res.json();
    },
    triggerSocialGeneration: async () => {
        const res = await fetch(`${API_URL}/admin/social/trigger`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to trigger generation");
        }
        return res.json();
    }
};
